import {
	find as _find,
	forIn as _forIn,
	cloneDeep as _cloneDeep,
	isEqual as _isEqual,
} from 'lodash';
import {Action as CommonAction, commonActions} from '@gisatcz/ptr-state';
import ActionTypes from '../constants/ActionTypes';
import Select from './Select';

// Actions
import interferometryDataActions from './insar/InterferometryData/actions';
import typesActions from './insar/Types/actions';
import orbitalTrajectoriesActions from './insar/OrbitalTrajectories/actions';
import visualizationsActions from './insar/Visualizations/actions';

// insar specific data
import typesData from '../data/insar/types';
import orbitalTrajectoriesData from '../data/insar/orbitalTrajectories';
import visualizationsData from '../data/insar/visualizations';

// metadata
import attributesData from '../data/attributes/attributes';
import attributeSetsData from '../data/attributes/attributeSets';
import stylesData from '../data/styles/styles';
import backgroundLayers from '../data/layers/backgroundLayers';

const filteredOutStyle = {
	fillOpacity: 0,
	outlineOpacity: 0,
};

function useInterferometryMapData(forceLoad) {
	return (dispatch, getState) => {
		const state = getState();
		const fidColumnName = Select.app.getLocalConfiguration(
			state,
			'data.interferometry.fidColumnName'
		);
		const type = Select.insar.types.getActiveKey(state);
		const tracks =
			Select.insar.orbitalTrajectories.getActiveTrajectoriesTracks(state);

		return dispatch(
			interferometryDataActions.ensureDataForMap(
				type,
				tracks,
				fidColumnName,
				forceLoad
			)
		)
			.then(() => {
				dispatch(refreshInterferometryMapData(type, tracks));
				dispatch(useInterferometryPointStatisticsForSelectedFeatures());
			})
			.catch(err => {
				throw new Error(err);
			});
	};
}

function refreshInterferometryMapData(type, tracks) {
	return (dispatch, getState) => {
		const state = getState();
		const features =
			Select.insar.interferometryData.getAllFeaturesByTypeAndTrackKeysSorted(
				state,
				type,
				tracks
			);

		const mapKey = Select.app.getLocalConfiguration(
			state,
			'data.interferometry.mapKey'
		);
		const smallMapKey = Select.app.getLocalConfiguration(
			state,
			'data.interferometry.smallMapKey'
		);
		const layerKey = Select.app.getLocalConfiguration(
			state,
			'data.interferometry.layerKeys.points'
		);
		const smallMapLayerKey = Select.app.getLocalConfiguration(
			state,
			'data.interferometry.layerKeys.pointsSmall'
		);

		dispatch(
			CommonAction.maps.setMapLayerOption(
				mapKey,
				layerKey,
				'features',
				features || []
			)
		);

		dispatch(
			CommonAction.maps.setMapLayerOption(
				smallMapKey,
				smallMapLayerKey,
				'features',
				features || []
			)
		);

		dispatch(setInterferometryMapLayerStyle());

		dispatch(CommonAction.components.set('loadingOverlay', 'open', false));
	};
}

function useInterferometryPointStatistics(featureKeys) {
	return (dispatch, getState) => {
		const state = getState();

		// expecting only one feature to be selected
		const selectedFeatureKey = featureKeys[0];

		// get featureData
		const fidColumnName = Select.app.getLocalConfiguration(
			state,
			'data.interferometry.fidColumnName'
		);
		const type = Select.insar.types.getActiveKey(state);

		const feature =
			Select.insar.interferometryData.getFeatureByTypeAndFeatureKey(
				state,
				type,
				selectedFeatureKey
			);

		if (feature?.properties?.trackKey) {
			dispatch(
				interferometryDataActions.ensureDataForPointStatistics(
					type,
					null,
					feature.properties.trackKey,
					fidColumnName,
					selectedFeatureKey
				)
			);
		}
	};
}

function useInterferometryPointStatisticsForSelectedFeatures() {
	return (dispatch, getState) => {
		const activeSelection = Select.selections.getActive(getState());
		const featureKeys = activeSelection?.data?.featureKeysFilter?.keys;
		if (featureKeys) {
			dispatch(useInterferometryPointStatistics(featureKeys));
		}
	};
}

function initApp(view) {
	return dispatch => {
		const insarActions = {
			types: typesActions,
			visualizations: visualizationsActions,
			orbitalTrajectories: orbitalTrajectoriesActions,
		};

		// add view
		dispatch(CommonAction.views.add(view));

		// add metadata
		dispatch(CommonAction.attributes.add(Object.values(attributesData.byKey)));
		dispatch(
			commonActions.add(ActionTypes.ATTRIBUTE_SETS)(
				Object.values(attributeSetsData.byKey)
			)
		);
		dispatch(CommonAction.styles.add(Object.values(stylesData.byKey)));

		// add insar-specific metadata
		dispatch(typesActions.updateStore(typesData));
		dispatch(orbitalTrajectoriesActions.updateStore(orbitalTrajectoriesData));
		dispatch(visualizationsActions.updateStore(visualizationsData));

		// apply view
		dispatch(
			CommonAction.components.updateStateFromView(view?.data?.state?.components)
		);
		dispatch(CommonAction.maps.updateStateFromView(view?.data?.state?.maps));
		dispatch(
			CommonAction.selections.updateStateFromViewWithData(
				view?.data?.state?.selections
			)
		);
		const insarDataView = view?.data?.state?.insar;
		if (insarDataView) {
			_forIn(insarDataView, (substate, storeKey) => {
				if (Object.hasOwn(insarActions[storeKey], 'updateStateFromView')) {
					dispatch(insarActions[storeKey].updateStateFromView(substate));
				}
			});
		}

		// use data
		dispatch(useInterferometryMapData());
	};
}

function changeActiveDivision(division) {
	return (dispatch, getState) => {
		const state = getState();
		dispatch(
			CommonAction.components.set('controlPanel', 'activeDivision', division)
		);

		if (division === 'zones') {
			const zonesActiveMapKey = Select.app.getLocalConfiguration(
				state,
				'data.zones.mapKey'
			);
			const zonesActiveSelectionKey = Select.app.getLocalConfiguration(
				state,
				'data.zones.selectionKey'
			);
			dispatch(CommonAction.selections.setActiveKey(zonesActiveSelectionKey));
			dispatch(CommonAction.maps.setActiveMapKey(zonesActiveMapKey));
		} else if (division === 'interferometry') {
			const interferometryActiveMapKey = Select.app.getLocalConfiguration(
				state,
				'data.interferometry.mapKey'
			);
			const interferometryActiveSelectionKey = Select.app.getLocalConfiguration(
				state,
				'data.interferometry.selectionKey'
			);
			dispatch(
				CommonAction.selections.setActiveKey(interferometryActiveSelectionKey)
			);
			dispatch(CommonAction.maps.setActiveMapKey(interferometryActiveMapKey));
		}
	};
}

function setInterferometryMapLayerStyle() {
	return (dispatch, getState) => {
		const state = getState();
		const mapKey = Select.app.getLocalConfiguration(
			state,
			'data.interferometry.mapKey'
		);
		const smallMapKey = Select.app.getLocalConfiguration(
			state,
			'data.interferometry.smallMapKey'
		);
		const layerKey = Select.app.getLocalConfiguration(
			state,
			'data.interferometry.layerKeys.points'
		);
		const smallMapLayerKey = Select.app.getLocalConfiguration(
			state,
			'data.interferometry.layerKeys.pointsSmall'
		);
		const styles = Select.insar.getActiveVisualizationStylesByActiveType(state);

		if (styles) {
			const mapLayer = Select.maps.getMapLayerStateByMapKeyAndLayerKey(
				state,
				mapKey,
				layerKey
			);

			if (mapLayer) {
				let renderAs = _cloneDeep(mapLayer.options.renderAs);
				renderAs[0].options.style = getStyleDefinitionWithoutFilteredValues(
					state,
					styles.asPoints?.data?.definition
				);
				dispatch(
					CommonAction.maps.setMapLayerOption(
						mapKey,
						layerKey,
						'renderAs',
						renderAs
					)
				);
			}

			dispatch(
				CommonAction.maps.setMapLayerOption(
					mapKey,
					layerKey,
					'style',
					getStyleDefinitionWithoutFilteredValues(
						state,
						styles.asMarkers?.data?.definition
					)
				)
			);

			dispatch(
				CommonAction.maps.setMapLayerOption(
					smallMapKey,
					smallMapLayerKey,
					'style',
					getStyleDefinitionWithoutFilteredValues(
						state,
						styles.asPointsSmall?.data?.definition
					)
				)
			);
		}
	};
}

function handleMapLayer(mapKey, layerKey, styleKey) {
	return (dispatch, getState) => {
		const state = getState();
		const configuration = Select.app.getLocalConfiguration(state, 'data');
		const activeMap = Select.maps.getActiveMap(state);
		const styles = Select.styles.getAllAsObject(state);
		const {bigSmallMapPairs} = configuration;

		const smallMapKey = bigSmallMapPairs.maps[mapKey];
		const smallMapLayerKey = bigSmallMapPairs.layers[layerKey];
		const smallMapStyleKey = bigSmallMapPairs.styles[styleKey];

		dispatch(
			CommonAction.app.setLocalConfiguration(
				`data.layerStyles.${layerKey}.active`,
				styleKey
			)
		);

		if (!styleKey) {
			dispatch(CommonAction.maps.removeMapLayer(mapKey, layerKey));
			dispatch(CommonAction.maps.removeMapLayer(smallMapKey, smallMapLayerKey));
		} else {
			const currentLayers = activeMap?.data?.layers || [];
			const existingLayer = _find(
				currentLayers,
				layer => layer.key === layerKey
			);

			if (!existingLayer) {
				const vectorLayers = Select.insar.getAvailableVectorLayers(state);
				const layerOrder = configuration.layerOrder[mapKey];
				const index = getLayerIndex(
					layerKey,
					layerOrder,
					currentLayers.map(layer => layer.key)
				);

				const layerData = _find(vectorLayers, layer => layer.key === layerKey);
				const smallMapLayerData = _find(
					vectorLayers,
					layer => layer.key === smallMapLayerKey
				);

				dispatch(
					CommonAction.maps.addMapLayerToIndex(mapKey, layerData, index)
				);
				dispatch(
					CommonAction.maps.addMapLayerToIndex(
						smallMapKey,
						smallMapLayerData,
						index
					)
				);
			}

			const styleDefinition = styles[styleKey]?.data.definition;
			const smallMapStyleDefinition = styles[smallMapStyleKey]?.data.definition;

			dispatch(
				CommonAction.maps.setMapLayerOption(
					mapKey,
					layerKey,
					'style',
					styleDefinition
				)
			);
			dispatch(
				CommonAction.maps.setMapLayerOption(
					smallMapKey,
					smallMapLayerKey,
					'style',
					smallMapStyleDefinition
				)
			);
		}
	};
}

function setBackgroundLayer(key) {
	return (dispatch, getState) => {
		const state = getState();
		const zonesMapSetKey = Select.app.getLocalConfiguration(
			state,
			'data.zones.mapSetKey'
		);
		const interferometryMapSetKey = Select.app.getLocalConfiguration(
			state,
			'data.interferometry.mapSetKey'
		);

		const definition = backgroundLayers?.[key];
		dispatch(
			CommonAction.maps.setMapSetBackgroundLayer(zonesMapSetKey, definition)
		);
		dispatch(
			CommonAction.maps.setMapSetBackgroundLayer(
				interferometryMapSetKey,
				definition
			)
		);
	};
}

function setWmsLayers(layers) {
	return (dispatch, getState) => {
		const state = getState();
		const zonesMapSetKey = Select.app.getLocalConfiguration(
			state,
			'data.zones.mapSetKey'
		);
		const interferometryMapSetKey = Select.app.getLocalConfiguration(
			state,
			'data.interferometry.mapSetKey'
		);

		dispatch(CommonAction.maps.setMapSetLayers(zonesMapSetKey, layers));
		dispatch(
			CommonAction.maps.setMapSetLayers(interferometryMapSetKey, layers)
		);
	};
}

function setMapSetCenterByMapSetCenter(fromSection, toSection) {
	return (dispatch, getState) => {
		const state = getState();
		const fromMapSetKey = Select.app.getLocalConfiguration(
			state,
			`data.${fromSection}.mapSetKey`
		);
		const toMapSetKey = Select.app.getLocalConfiguration(
			state,
			`data.${toSection}.mapSetKey`
		);

		const fromView = Select.maps.getMapSetView(getState(), fromMapSetKey);
		if (fromView?.center) {
			dispatch(
				CommonAction.maps.updateSetView(toMapSetKey, {
					center: fromView.center,
				})
			);
		}
	};
}

// helpers
function getLayerIndex(layerKey, layersOrder, currentLayers) {
	const indexInOrder = layersOrder.indexOf(layerKey);
	if (currentLayers.length) {
		let i = 0;
		for (i; i < currentLayers.length; i++) {
			const currentItemIndex = layersOrder.indexOf(currentLayers[i]);
			const nextItemIndex = layersOrder.indexOf(currentLayers[i + 1]);

			if (currentItemIndex > indexInOrder) {
				return i;
			}
			if (currentItemIndex < indexInOrder && nextItemIndex >= indexInOrder) {
				return i + 1;
			}
		}
		return i;
	} else {
		return 0;
	}
}

/// creats style rules - sets opacity to 0 to the turned off intervals
/**
 * @param state {Object}
 * @param styleDefinition {Object} Panther style definition
 */
function getStyleDefinitionWithoutFilteredValues(state, styleDefinition) {
	const activeVisualization = Select.insar.visualizations.getActive(state);
	const filteredOutValues = activeVisualization?.data?.filteredOutValues;
	const filteredOutIntervals = activeVisualization?.data?.filteredOutIntervals;
	const filteredOutNoData = activeVisualization?.data?.filteredOutNoData;

	if (filteredOutValues || filteredOutIntervals || filteredOutNoData) {
		let updatedDefinition = _cloneDeep(styleDefinition);
		const defaultStyle = updatedDefinition.rules[0].styles[0];

		if (filteredOutNoData) {
			updatedDefinition.rules[0].styles[0] = {
				...defaultStyle,
				...filteredOutStyle,
			};
		}

		if (filteredOutValues || filteredOutNoData) {
			const values = updatedDefinition.rules[0].styles[1].attributeValues;
			if (values) {
				_forIn(values, (originalStyle, value) => {
					let filteredStyle = {};

					if (filteredOutValues) {
						filteredOutValues.forEach(filteredOutValue => {
							if (filteredOutValue === value) {
								filteredStyle = filteredOutStyle;
							}
						});
					}

					updatedDefinition.rules[0].styles[1].attributeValues[value] = {
						...defaultStyle,
						...originalStyle,
						...filteredStyle,
					};
				});
			}
		}

		if (filteredOutIntervals || filteredOutNoData) {
			const classes = updatedDefinition.rules[0].styles[1].attributeClasses;
			if (classes) {
				updatedDefinition.rules[0].styles[1].attributeClasses = classes.map(
					attributeClass => {
						let style = {};

						if (filteredOutIntervals) {
							filteredOutIntervals.forEach(interval => {
								if (_isEqual(interval, attributeClass.interval)) {
									style = filteredOutStyle; //fill opacity
								}
							});
						}
						return {
							...defaultStyle,
							...attributeClass,
							...style,
						};
					}
				);
			}
		}

		return updatedDefinition;
	} else {
		return styleDefinition;
	}
}

function getStyleDefinitionWithNewIntervals(activeStyle, interval) {
	const updatedDefinition = _cloneDeep(activeStyle);
	const defaultStyle = updatedDefinition.data.definition.rules[0].styles[0];

	const interval1 = interval[0]; // a
	const interval2 = interval[1]; // b

	const classes =
		updatedDefinition.data.definition.rules[0].styles[1].attributeClasses;

	const getComputedInterval = () => {
		return (interval2 - interval1) / 10;
	};

	const getInterval1 = index => {
		if (classes.length - 1 === index) {
			return interval2;
		} else if (index === 0) {
			return -1000;
		} else {
			return (
				Math.round((interval1 + (index - 1) * getComputedInterval()) * 100) /
				100
			);
		}
	};

	const getInterval2 = index => {
		if (index === 0) {
			return interval1;
		} else if (classes.length - 1 === index) {
			return 1000;
		} else {
			return (
				Math.round((interval1 + index * getComputedInterval()) * 100) / 100
			);
		}
	};

	const getName = index => {
		if (index === 0) {
			return '< ' + interval1 + ' (Subsidence)';
		} else if (classes.length - 1 === index) {
			return '(Uplift) ' + interval2 + ' <';
		} else {
			return null;
		}
	};

	updatedDefinition.data.definition.rules[0].styles[1].attributeClasses =
		classes.map((attributeClass, index) => {
			return {
				...defaultStyle,
				...attributeClass,
				interval: [getInterval1(index), getInterval2(index)],
				name: getName(index),
			};
		});
	return updatedDefinition;
}

function updateStylesWithSelectedInterval(interval) {
	return (dispatch, getState) => {
		const state = getState();
		const styles = Select.insar.getActiveVisualizationStylesByActiveType(state);
		const newStyles = [];

		Object.values(stylesData.byKey).map(style => {
			if (style.key === styles.asPoints.key) {
				newStyles.push(
					getStyleDefinitionWithNewIntervals(styles.asPoints, interval)
				);
			} else if (style.key === styles.asMarkers.key) {
				newStyles.push(
					getStyleDefinitionWithNewIntervals(styles.asMarkers, interval)
				);
			} else if (style.key === styles.asPointsSmall.key) {
				newStyles.push(
					getStyleDefinitionWithNewIntervals(styles.asPointsSmall, interval)
				);
			} else {
				newStyles.push(style);
			}
		});

		dispatch(CommonAction.styles.add(newStyles));
	};
}

export default {
	...CommonAction,
	insar: {
		types: typesActions,
		orbitalTrajectories: orbitalTrajectoriesActions,
		visualizations: visualizationsActions,
		interferometryData: interferometryDataActions,

		initApp,
		changeActiveDivision,

		handleMapLayer,
		setWmsLayers,
		setInterferometryMapLayerStyle,
		setBackgroundLayer,
		setMapSetCenterByMapSetCenter,
		useInterferometryMapData,
		useInterferometryPointStatistics,
		updateStylesWithSelectedInterval,

		// helpers
		getLayerIndex,
	},
};
